import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, X, Image, Video } from "lucide-react";
import { uploadToStorage, removeFromStorage } from "@/components/campaign/utils/fileUpload";

interface CampaignCreativeEditorProps {
  campaign: any;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export const CampaignCreativeEditor = ({ campaign, isOpen, onClose, onUpdate }: CampaignCreativeEditorProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [creativeUrls, setCreativeUrls] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize creative URLs when campaign changes
  useEffect(() => {
    if (campaign) {
      console.log('Campaign data:', campaign);
      console.log('Creative URLs from campaign:', campaign.creative_urls);
      
      const urls = campaign.creative_urls || [];
      console.log('Setting creative URLs:', urls);
      setCreativeUrls(urls);
    }
  }, [campaign]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    // SECURITY: Validate file types and sizes
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo'];
    const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];

    for (const file of files) {
      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: language === 'ar' ? 'نوع ملف غير مدعوم' : 'Unsupported file type',
          description: language === 'ar' 
            ? `الملف ${file.name} غير مدعوم. يُسمح فقط بالصور والفيديوهات`
            : `File ${file.name} is not supported. Only images and videos are allowed`,
          variant: "destructive",
        });
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: language === 'ar' ? 'الملف كبير جداً' : 'File too large',
          description: language === 'ar'
            ? `الملف ${file.name} يتجاوز الحد الأقصى 50 ميجابايت`
            : `File ${file.name} exceeds maximum size of 50MB`,
          variant: "destructive",
        });
        return;
      }

      // Validate file name (prevent path traversal)
      if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
        toast({
          title: language === 'ar' ? 'اسم ملف غير صالح' : 'Invalid file name',
          description: language === 'ar'
            ? 'اسم الملف يحتوي على أحرف غير مسموح بها'
            : 'File name contains invalid characters',
          variant: "destructive",
        });
        return;
      }
    }

    try {
      setIsUploading(true);
      
      for (const file of files) {
        // Sanitize filename and add timestamp for uniqueness
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `${Date.now()}-${sanitizedName}`;
        const url = await uploadToStorage(file, fileName);
        setCreativeUrls(prev => [...prev, url]);
      }

      toast({
        title: language === 'ar' ? 'تم رفع الملف بنجاح' : 'File uploaded successfully',
        description: language === 'ar' ? 'تم رفع الملف الإبداعي بنجاح' : 'Creative file uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: language === 'ar' ? 'خطأ في الرفع' : 'Upload Error',
        description: language === 'ar' ? 'فشل في رفع الملف' : 'Failed to upload file',
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveCreative = async (url: string, index: number) => {
    try {
      await removeFromStorage(url);
      setCreativeUrls(prev => prev.filter((_, i) => i !== index));
      
      toast({
        title: language === 'ar' ? 'تم حذف الملف' : 'File removed',
        description: language === 'ar' ? 'تم حذف الملف الإبداعي بنجاح' : 'Creative file removed successfully',
      });
    } catch (error) {
      console.error('Error removing file:', error);
      toast({
        title: language === 'ar' ? 'خطأ في الحذف' : 'Delete Error',
        description: language === 'ar' ? 'فشل في حذف الملف' : 'Failed to delete file',
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // SECURITY: Validate creative URLs before saving
      const validUrls = creativeUrls.filter(url => {
        try {
          const urlObj = new URL(url);
          // Only allow URLs from our Supabase storage
          return urlObj.hostname.includes('supabase.co');
        } catch {
          return false;
        }
      });

      if (validUrls.length !== creativeUrls.length) {
        toast({
          title: language === 'ar' ? 'خطأ في التحقق' : 'Validation Error',
          description: language === 'ar' 
            ? 'تم اكتشاف روابط غير صالحة' 
            : 'Invalid URLs detected and removed',
          variant: "destructive",
        });
        setCreativeUrls(validUrls);
        return;
      }

      // SECURITY: Verify campaign ownership before update
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data: campaignData, error: fetchError } = await supabase
        .from('campaigns')
        .select('created_by')
        .eq('id', campaign.id)
        .maybeSingle();

      if (fetchError || !campaignData) {
        throw new Error('Campaign not found or access denied');
      }

      if (campaignData.created_by !== user.id) {
        throw new Error('Access denied - you do not own this campaign');
      }

      // Update the campaign
      const { error } = await supabase
        .from('campaigns')
        .update({ creative_urls: creativeUrls })
        .eq('id', campaign.id);

      if (error) throw error;

      toast({
        title: language === 'ar' ? 'تم الحفظ بنجاح' : 'Saved successfully',
        description: language === 'ar' ? 'تم تحديث المواد الإبداعية بنجاح' : 'Creatives updated successfully',
      });

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving creatives:', error);
      toast({
        title: language === 'ar' ? 'خطأ في الحفظ' : 'Save Error',
        description: error instanceof Error ? error.message : (language === 'ar' ? 'فشل في حفظ التغييرات' : 'Failed to save changes'),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getFileType = (url: string) => {
    const extension = url.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')) return 'image';
    if (['mp4', 'mpeg', '3gp', 'avi', 'mov'].includes(extension || '')) return 'video';
    return 'unknown';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {language === 'ar' ? 'تعديل المواد الإبداعية' : 'Edit Campaign Creatives'}
          </DialogTitle>
          <DialogDescription>
            {language === 'ar' 
              ? 'يمكنك إضافة أو حذف المواد الإبداعية للحملة'
              : 'Add or remove creative materials for your campaign'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Creatives */}
          <div>
            <h3 className="text-lg font-medium mb-4">
              {language === 'ar' ? 'المواد الإبداعية الحالية' : 'Current Creatives'}
            </h3>
            
            {creativeUrls.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {language === 'ar' ? 'لا توجد مواد إبداعية' : 'No creatives uploaded'}
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {creativeUrls.map((url, index) => (
                  <div key={index} className="relative group border rounded-lg overflow-hidden">
                    {getFileType(url) === 'image' ? (
                      <img 
                        src={url} 
                        alt={`Creative ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                    ) : getFileType(url) === 'video' ? (
                      <div className="w-full h-32 bg-muted/50 dark:bg-muted/20 flex items-center justify-center">
                        <Video className="h-8 w-8 text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="w-full h-32 bg-muted/50 dark:bg-muted/20 flex items-center justify-center">
                        <Image className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                      onClick={() => handleRemoveCreative(url, index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload New Creative */}
          <div>
            <h3 className="text-lg font-medium mb-4">
              {language === 'ar' ? 'إضافة مواد إبداعية جديدة' : 'Add New Creatives'}
            </h3>
            
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
              <p className="mb-4 text-muted-foreground">
                {language === 'ar' 
                  ? 'اختر الملفات أو اسحبها هنا'
                  : 'Choose files or drag them here'}
              </p>
              <Input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.mp4,.mpeg,.3gp,.avi,.mov"
                onChange={handleFileUpload}
                disabled={isUploading}
                className="hidden"
                id="creative-upload"
              />
              <Button 
                variant="outline" 
                onClick={() => document.getElementById('creative-upload')?.click()}
                disabled={isUploading}
              >
                {isUploading 
                  ? (language === 'ar' ? 'جاري الرفع...' : 'Uploading...')
                  : (language === 'ar' ? 'اختيار الملفات' : 'Choose Files')
                }
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-primary hover:bg-primary/90"
            >
              {isSaving 
                ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
